import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Edit, FileText, Zap, Bot } from 'lucide-react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { talentTemplates } from '../services/apiService';
import { useAuth } from '../hooks/useAuth';
import { Loader } from '../components/UI/LoadingSpinner';
import TalentTemplateModal from '../components/TalentTemplate/TalentTemplateModal';
import Avatar from '../components/UI/Avatar';
import HistoryTab from '../components/UI/HistoryTab';

const BLOCK_LABELS = {
  responsibilities: 'Responsibilities',
  cv: 'CV / Experience',
  media: 'Media',
  languages: 'Languages',
  tools: 'Tools',
  professions: 'Professions'
};

const UserCell = ({ user, fallback }) => {
  if (!user) return <span className="text-light-text-tertiary dark:text-dark-text-tertiary">—</span>;
  const displayName = user.name || `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email || fallback || '—';
  return (
    <div className="flex min-w-0 items-center gap-2">
      <Avatar avatar={user.avatar} name={displayName} size="xs" />
      <span className="min-w-0 break-all">{displayName}</span>
    </div>
  );
};

const TalentTemplateDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const [template, setTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('details');
  const canUpdate = hasPermission('talent-service', 'talent_template', 'update');

  useEffect(() => {
    fetchTemplate();
  }, [id]);

  const fetchTemplate = async () => {
    setLoading(true);
    try {
      const res = await talentTemplates.getById(id);
      setTemplate(res?.data || res);
    } catch (err) {
      toast.error('Failed to load template');
      navigate('/talent-templates');
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <Loader />;
  if (!template) return null;

  const blocks = template.blocks_config || {};

  return (
    <div className="min-h-full bg-light-bg-primary p-4 dark:bg-dark-bg-primary sm:p-6">
      {/* Header */}
      <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/talent-templates')}
            className="p-1.5 text-light-text-tertiary dark:text-dark-text-tertiary hover:text-light-text-primary dark:hover:text-dark-text-primary transition-colors"
          >
            <ArrowLeft size={20} />
          </button>
          <FileText className="text-blue-600" size={24} />
          <h1 className="text-2xl font-bold text-light-text-primary dark:text-dark-text-primary">
            {template.name}
          </h1>
          {template.status && (
            <span
              className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium"
              style={{ backgroundColor: (template.status.color || '#6B7280') + '22', color: template.status.color || '#6B7280' }}
            >
              {template.status.name}
            </span>
          )}
        </div>
        {canUpdate && (
          <button
            onClick={() => setModalOpen(true)}
            className="flex items-center gap-2 px-4 py-2 text-sm text-white bg-primary-600 rounded-md hover:bg-primary-700"
          >
            <Edit size={16} />
            Edit
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-4 border-b border-light-border-primary dark:border-dark-border-primary">
        {['details', 'history'].map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 text-sm font-medium capitalize border-b-2 -mb-px transition-colors ${
              activeTab === tab
                ? 'border-primary-600 text-primary-600 dark:text-primary-400 dark:border-primary-400'
                : 'border-transparent text-light-text-tertiary dark:text-dark-text-tertiary hover:text-light-text-primary dark:hover:text-dark-text-primary'
            }`}
          >
            {tab}
          </button>
        ))}
      </div>

      {activeTab === 'history' && (
        <div className="rounded-lg border border-light-border-primary dark:border-dark-border-primary overflow-hidden">
          <HistoryTab
            entityId={id}
            fetchEventsFunction={talentTemplates.getEvents}
            fetchStatsFunction={talentTemplates.getEventStats}
          />
        </div>
      )}

      {activeTab === 'details' && <>

      {/* Posting criteria */}
      <div className="mb-4 rounded-lg border border-light-border-primary dark:border-dark-border-primary p-4">
        <div className="flex items-center gap-2 mb-3">
          <Zap size={12} className="text-light-text-tertiary dark:text-dark-text-tertiary shrink-0" />
          <span className="text-xs font-semibold text-light-text-secondary dark:text-dark-text-secondary uppercase tracking-wide">
            Posting criteria
          </span>
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div className="bg-light-bg-secondary dark:bg-dark-bg-secondary rounded-md p-3">
            <p className="text-xs text-light-text-tertiary dark:text-dark-text-tertiary mb-1">Platform</p>
            <p className="text-sm font-medium text-light-text-primary dark:text-dark-text-primary">
              {template.tool?.name || template.tool?.title || (template.tool_id ? `#${template.tool_id}` : <span className="text-light-text-tertiary dark:text-dark-text-tertiary font-normal">Any</span>)}
            </p>
          </div>
          <div className="bg-light-bg-secondary dark:bg-dark-bg-secondary rounded-md p-3">
            <p className="text-xs text-light-text-tertiary dark:text-dark-text-tertiary mb-1">Inner Client</p>
            <p className="text-sm font-medium text-light-text-primary dark:text-dark-text-primary">
              {template.inner_client?.name || (template.inner_client_id ? `#${template.inner_client_id}` : <span className="text-light-text-tertiary dark:text-dark-text-tertiary font-normal">Any</span>)}
            </p>
          </div>
        </div>
      </div>

      {/* Prompt */}
      <div className="mb-4 rounded-lg border border-light-border-primary dark:border-dark-border-primary p-4">
        <div className="flex items-center gap-2 mb-3">
          <Bot size={12} className="text-light-text-tertiary dark:text-dark-text-tertiary shrink-0" />
          <span className="text-xs font-semibold text-light-text-secondary dark:text-dark-text-secondary uppercase tracking-wide">
            Prompt for post generation
          </span>
        </div>
        {template.prompt ? (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Link
                to={`/prompts/${template.prompt.id}`}
                className="text-sm font-medium text-primary-600 dark:text-primary-400 hover:underline"
              >
                {template.prompt.name}
              </Link>
              {template.prompt.is_default && (
                <span className="text-xs px-1.5 py-0.5 rounded bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300">
                  default
                </span>
              )}
            </div>
            {template.prompt.body && (
              <p className="text-xs text-light-text-tertiary dark:text-dark-text-tertiary line-clamp-3 font-mono leading-relaxed">
                {template.prompt.body}
              </p>
            )}
          </div>
        ) : (
          <p className="text-sm text-light-text-tertiary dark:text-dark-text-tertiary">
            Not set — default prompt will be selected automatically
          </p>
        )}
      </div>

      {/* Content Blocks */}
      <div className="mb-4 bg-light-bg-secondary dark:bg-dark-bg-secondary rounded-lg p-4">
        <p className="text-xs font-medium text-light-text-tertiary dark:text-dark-text-tertiary uppercase tracking-wider mb-3">
          Content Blocks
        </p>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
          {Object.entries(BLOCK_LABELS).map(([key, label]) => (
            <div key={key} className="flex items-center gap-2">
              <span className={`w-3 h-3 rounded-sm shrink-0 ${blocks[key] ? 'bg-green-500' : 'bg-light-border-primary dark:bg-dark-border-primary'}`} />
              <span className={`text-sm ${blocks[key] ? 'text-light-text-primary dark:text-dark-text-primary' : 'text-light-text-tertiary dark:text-dark-text-tertiary line-through'}`}>
                {label}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Stats */}
      <div className="mb-4 bg-light-bg-secondary dark:bg-dark-bg-secondary rounded-lg px-4 py-3 flex items-center gap-6">
        <div>
          <span className="text-xs text-light-text-tertiary dark:text-dark-text-tertiary">Posts created</span>
          <p className="text-xl font-bold text-light-text-primary dark:text-dark-text-primary">{template.sum_posts || 0}</p>
        </div>
      </div>

      {/* Dates & Author */}
      <div className="mb-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div className="flex min-w-0 flex-wrap items-start justify-between gap-2 rounded-lg bg-light-bg-secondary px-4 py-2.5 dark:bg-dark-bg-secondary">
          <span className="shrink-0 text-xs text-light-text-tertiary dark:text-dark-text-tertiary">Created by</span>
          <div className="min-w-0 max-w-full text-sm text-light-text-secondary dark:text-dark-text-secondary">
            <UserCell user={template.createdByUser} fallback={template.created_by} />
          </div>
        </div>
        <div className="flex min-w-0 flex-wrap items-start justify-between gap-2 rounded-lg bg-light-bg-secondary px-4 py-2.5 dark:bg-dark-bg-secondary">
          <span className="shrink-0 text-xs text-light-text-tertiary dark:text-dark-text-tertiary">Updated by</span>
          <div className="min-w-0 max-w-full text-sm text-light-text-secondary dark:text-dark-text-secondary">
            <UserCell user={template.updatedByUser} fallback={template.updated_by} />
          </div>
        </div>
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div className="flex min-w-0 flex-wrap items-start justify-between gap-2 rounded-lg bg-light-bg-secondary px-4 py-2.5 dark:bg-dark-bg-secondary">
          <span className="shrink-0 text-xs text-light-text-tertiary dark:text-dark-text-tertiary">Created</span>
          <span className="min-w-0 break-words text-right text-sm text-light-text-secondary dark:text-dark-text-secondary">
            {template.created_at ? new Date(template.created_at).toLocaleString() : '—'}
          </span>
        </div>
        <div className="flex min-w-0 flex-wrap items-start justify-between gap-2 rounded-lg bg-light-bg-secondary px-4 py-2.5 dark:bg-dark-bg-secondary">
          <span className="shrink-0 text-xs text-light-text-tertiary dark:text-dark-text-tertiary">Updated</span>
          <span className="min-w-0 break-words text-right text-sm text-light-text-secondary dark:text-dark-text-secondary">
            {template.updated_at ? new Date(template.updated_at).toLocaleString() : '—'}
          </span>
        </div>
      </div>

      </>}

      <TalentTemplateModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        template={template}
        onSaved={() => { setModalOpen(false); fetchTemplate(); }}
      />
    </div>
  );
};

export default TalentTemplateDetail;
